import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  CalendarRange,
  CheckCircle2,
  Loader2,
  Play,
  Power,
  UserRound,
  Workflow,
  XCircle,
} from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  usePipelineExecutionStatus,
  useStartPipeline,
} from "@/hooks/api/usePipeline";
import {
  simulationKeys,
  useSetCurrentSimulation,
  useSimulationDetail,
} from "@/hooks/api/useSimulations";
import { useQueryClient } from "@tanstack/react-query";
import { isHandledError } from "@/lib/api/errors";
import type { PipelineExecutionStatus, StepDetail } from "@/services/pipeline.service";
import type { SimulationConfig } from "@/services/simulations.service";

function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value || "—";
  return d.toLocaleDateString("pt-BR");
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("pt-BR");
}

function formatDuration(seconds: number | null): string {
  if (seconds == null || Number.isNaN(seconds)) return "—";
  if (seconds < 60) {
    const rounded = Math.round(seconds * 10) / 10;
    return `${rounded}s`;
  }
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

type PipelineStepState = "pending" | "running" | "completed" | "failed";

function getPipelineStepState(
  step: string,
  exec: PipelineExecutionStatus | undefined,
): PipelineStepState {
  if (!exec) return "pending";
  if (exec.steps_failed?.includes(step)) return "failed";
  if (exec.steps_completed?.includes(step)) return "completed";
  const st = exec.status?.toLowerCase() ?? "";
  if (
    (st === "running" || st === "pending" || st === "queued" || st === "starting") &&
    exec.current_step === step
  ) {
    return "running";
  }
  return "pending";
}

function getExecutionStatusLabel(status: string): string {
  const s = status.toLowerCase();
  if (s === "running") return "Em execução";
  if (s === "completed" || s === "success") return "Concluída";
  if (s === "failed" || s === "error") return "Falhou";
  if (s === "pending" || s === "queued" || s === "starting") return "Aguardando";
  return status || "—";
}

function getExecutionStatusBadgeClass(status: string): string {
  const s = status.toLowerCase();
  if (s === "running") return "bg-[#00646B] text-white";
  if (s === "completed" || s === "success")
    return "bg-[#135045] text-[#88C0B2]";
  if (s === "failed" || s === "error")
    return "bg-[#93000A] text-white";
  if (s === "pending" || s === "queued" || s === "starting")
    return "bg-[#E8EFF0] text-[#3B494A]";
  return "bg-[#E8EFF0] text-[#3B494A]";
}

function pipelineConfigLabel(count: number): string {
  return count === 1 ? "1 etapa" : `${count} etapas`;
}

function pipelineStepLabel(state: PipelineStepState): string {
  switch (state) {
    case "completed":
      return "Concluída";
    case "running":
      return "Em execução";
    case "failed":
      return "Falhou";
    default:
      return "Pendente";
  }
}

function pipelineStepRowClass(state: PipelineStepState): string {
  switch (state) {
    case "completed":
      return "border-[#88C0B2] from-[#F0FAF7] to-white";
    case "running":
      return "border-[#00646B] from-[#E8F5F6] to-white ring-1 ring-[#00646B]/25";
    case "failed":
      return "border-[#E8A0A6] from-[#FFF8F8] to-white";
    default:
      return "border-[#BFD0D2] from-[#F7FAFB] to-white";
  }
}

function pipelineStepBadgeClass(state: PipelineStepState): string {
  switch (state) {
    case "completed":
      return "border-[#88C0B2] bg-[#F0FAF7] text-[#135045]";
    case "running":
      return "border-[#00646B] bg-[#E8F5F6] text-[#00646B]";
    case "failed":
      return "border-[#F5C2C7] bg-[#FFF5F5] text-[#93000A]";
    default:
      return "border-[#BFD0D2] bg-white text-[#6B797A]";
  }
}

function PipelineStepIcon({
  state,
  index,
}: {
  state: PipelineStepState;
  index: number;
}) {
  if (state === "completed") {
    return (
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#135045]/15 text-[#135045]">
        <CheckCircle2 className="h-4 w-4" />
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#93000A]/12 text-[#93000A]">
        <XCircle className="h-4 w-4" />
      </span>
    );
  }
  if (state === "running") {
    return (
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#00646B] text-white">
        <Loader2 className="h-4 w-4 animate-spin" />
      </span>
    );
  }
  return (
    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E8EFF0] text-[#3B494A] text-xs font-semibold">
      {index + 1}
    </span>
  );
}

function normalizePipelineSteps(
  pipelineConfig: unknown,
): string[] {
  if (Array.isArray(pipelineConfig)) {
    return pipelineConfig
      .filter((step): step is string => typeof step === "string" && step.trim().length > 0)
      .map((step) => step.trim());
  }
  if (pipelineConfig && typeof pipelineConfig === "object") {
    return Object.values(pipelineConfig)
      .filter((step): step is string => typeof step === "string" && step.trim().length > 0)
      .map((step) => step.trim());
  }
  return [];
}

export default function SimulationDetails() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const simulationId = Number(id);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error, refetch } = useSimulationDetail(simulationId);
  const setCurrentMutation = useSetCurrentSimulation();
  const startPipelineMutation = useStartPipeline();

  // executionId comes directly from the API — no sessionStorage
  const executionId = data?.last_pipeline_execution_id ?? null;

  const {
    data: execStatus,
    isError: execStatusIsError,
    error: execStatusError,
  } = usePipelineExecutionStatus(executionId);

  // Prefer the steps list from the execution status (reflects what was actually run)
  const pipelineSteps = useMemo(() => {
    if (execStatus?.pipeline_steps?.length) return execStatus.pipeline_steps;
    return normalizePipelineSteps(data?.pipeline_config);
  }, [execStatus?.pipeline_steps, data?.pipeline_config]);

  const handleStartPipeline = async () => {
    if (!simulationId || pipelineSteps.length === 0) return;
    try {
      const execId = await startPipelineMutation.mutateAsync({
        simulation_id: simulationId,
        steps: pipelineSteps,
      });
      // Update the cache immediately so executionId is available without waiting for refetch
      queryClient.setQueryData<SimulationConfig>(
        simulationKeys.detail(simulationId),
        (old) => old ? { ...old, last_pipeline_execution_id: execId } : old,
      );
      // Refetch in background to sync any other fields
      void queryClient.invalidateQueries({ queryKey: simulationKeys.detail(simulationId) });
      toast({ title: "Pipeline iniciada" });
    } catch (err: unknown) {
      if (!isHandledError(err)) {
        const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
        const msg =
          anyErr?.response?.data?.message ??
          anyErr?.message ??
          "Erro ao iniciar pipeline.";
        toast({ title: "Erro", description: msg, variant: "destructive" });
      }
    }
  };

  const handleSetCurrent = async () => {
    if (!simulationId) return;
    try {
      await setCurrentMutation.mutateAsync(simulationId);
      toast({ title: "Simulação ativada" });
      await refetch();
    } catch (err: any) {
      if (!isHandledError(err)) {
        const msg =
          err?.response?.data?.message ??
          err?.message ??
          "Erro ao ativar simulação.";
        toast({ title: "Erro", description: msg, variant: "destructive" });
      }
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Navigation />
      <main className="flex-1 flex flex-col">
        <div className="border-b border-[#BAC9CA] bg-white">
          <div className="flex items-center justify-between px-5 py-5">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/simulacoes")}
                className="w-12 h-12 rounded-full hover:bg-[#0000000D]"
              >
                <ArrowLeft className="w-6 h-6 text-[#151C1D]" />
              </Button>
              <h1 className="text-[28px] font-medium text-[#151C1D]">
                Detalhes da Simulação
              </h1>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/simulacoes/${simulationId}/resultados`)}
                disabled={isLoading || !data}
                className="h-12 px-4 rounded-xl border-[#BAC9CA] text-[#151C1D] hover:bg-[#EDF4F5]"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Ver resultados
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleStartPipeline}
                disabled={
                  isLoading ||
                  !data ||
                  pipelineSteps.length === 0 ||
                  startPipelineMutation.isPending ||
                  executionId != null
                }
                className="h-12 px-4 rounded-xl border-[#BAC9CA] text-[#151C1D] hover:bg-[#EDF4F5]"
              >
                <Play className="w-4 h-4 mr-2" />
                Iniciar pipeline
              </Button>
              <Button
                onClick={handleSetCurrent}
                disabled={
                  isLoading || !data || data.is_current || setCurrentMutation.isPending
                }
                className="h-12 px-4 rounded-xl bg-[#00646B] hover:bg-[#00646B]/90 text-white"
              >
                <Power className="w-4 h-4 mr-2" />
                Deixar ativa
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 px-5 py-9">
          {isLoading ? (
            <div className="text-[#3B494A]">Carregando...</div>
          ) : isError || !data ? (
            <div className="text-[#93000A]">{error?.message ?? "Erro ao carregar simulação."}</div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="rounded-2xl border border-[#BAC9CA] bg-white p-4">
                  <div className="text-xs text-[#3B494A]">Status</div>
                  <div className="mt-2">
                    <Badge
                      className={`h-6 px-2 rounded ${
                        data.is_current
                          ? "bg-[#135045] text-[#88C0B2]"
                          : "bg-[#E8EFF0] text-[#3B494A]"
                      } text-xs font-semibold uppercase`}
                    >
                      {data.is_current ? "ATIVA" : "INATIVA"}
                    </Badge>
                  </div>
                </div>
                <div className="rounded-2xl border border-[#BAC9CA] bg-white p-4">
                  <div className="text-xs text-[#3B494A]">Etapas da pipeline</div>
                  <div className="mt-2 text-2xl font-semibold text-[#151C1D]">
                    {pipelineSteps.length}
                  </div>
                </div>
                <div className="rounded-2xl border border-[#BAC9CA] bg-white p-4">
                  <div className="text-xs text-[#3B494A]">Base</div>
                  <div className="mt-2 text-sm font-medium text-[#151C1D]">
                    {formatDate(data.base_date_start)} - {formatDate(data.base_date_end)}
                  </div>
                </div>
                <div className="rounded-2xl border border-[#BAC9CA] bg-white p-4">
                  <div className="text-xs text-[#3B494A]">Forecast</div>
                  <div className="mt-2 text-sm font-medium text-[#151C1D]">
                    {formatDate(data.forecast_date_start)} - {formatDate(data.forecast_date_end)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="rounded-[24px] overflow-hidden border border-[#BAC9CA]">
                  <div className="bg-[#EDF4F5] px-6 py-4 border-b border-[#BAC9CA]">
                    <h2 className="text-lg font-medium text-[#151C1D]">Informações da simulação</h2>
                  </div>
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                    <div>
                      <div className="text-[#3B494A]">Nome</div>
                      <div className="text-[#151C1D] font-medium">{data.name || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[#3B494A]">ID da simulação</div>
                      <div className="text-[#151C1D] font-medium">{data.id}</div>
                    </div>
                    <div>
                      <div className="text-[#3B494A] flex items-center gap-1">
                        <CalendarRange className="w-3.5 h-3.5" />
                        Base (início)
                      </div>
                      <div className="text-[#151C1D] font-medium">{formatDate(data.base_date_start)}</div>
                    </div>
                    <div>
                      <div className="text-[#3B494A] flex items-center gap-1">
                        <CalendarRange className="w-3.5 h-3.5" />
                        Base (fim)
                      </div>
                      <div className="text-[#151C1D] font-medium">{formatDate(data.base_date_end)}</div>
                    </div>
                    <div>
                      <div className="text-[#3B494A] flex items-center gap-1">
                        <CalendarRange className="w-3.5 h-3.5" />
                        Forecast (início)
                      </div>
                      <div className="text-[#151C1D] font-medium">{formatDate(data.forecast_date_start)}</div>
                    </div>
                    <div>
                      <div className="text-[#3B494A] flex items-center gap-1">
                        <CalendarRange className="w-3.5 h-3.5" />
                        Forecast (fim)
                      </div>
                      <div className="text-[#151C1D] font-medium">{formatDate(data.forecast_date_end)}</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-[24px] overflow-hidden border border-[#BAC9CA]">
                  <div className="bg-[#EDF4F5] px-6 py-4 border-b border-[#BAC9CA]">
                    <h3 className="text-lg font-medium text-[#151C1D]">Metadata</h3>
                  </div>
                  <div className="p-6 space-y-4 text-sm">
                    <div>
                      <div className="text-[#3B494A] flex items-center gap-1">
                        <UserRound className="w-3.5 h-3.5" />
                        Criado por
                      </div>
                      <div className="text-[#151C1D] font-medium">{data.created_by || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[#3B494A]">Parâmetros</div>
                      <div className="text-[#151C1D] font-medium">
                        {Array.isArray(data.parameters) ? data.parameters.length : 0} itens
                      </div>
                    </div>
                    <div>
                      <div className="text-[#3B494A]">Status atual</div>
                      <div className="text-[#151C1D] font-medium">
                        {data.is_current ? "Em produção" : "Não ativa"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-[24px] overflow-hidden border border-[#BAC9CA] lg:col-span-2">
                  <div className="bg-[#EDF4F5] px-6 py-4 border-b border-[#BAC9CA] flex flex-wrap items-center gap-3 justify-between">
                    <div className="flex items-center gap-2">
                      <Workflow className="w-5 h-5 text-[#00646B]" />
                      <h3 className="text-lg font-medium text-[#151C1D]">
                        Pipeline da simulação
                      </h3>
                    </div>
                    {executionId ? (
                      <Badge
                        variant="outline"
                        className="text-xs font-medium border-[#BAC9CA] text-[#3B494A]"
                      >
                        Execução #{executionId}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="p-6 space-y-6">
                    {executionId ? (
                      <div className="rounded-2xl border border-[#BAC9CA] bg-[#F7FAFB] p-4 space-y-3">
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <div className="text-xs font-medium uppercase tracking-wide text-[#3B494A]">
                            Status da execução
                          </div>
                          {execStatus?.status ? (
                            <Badge
                              className={`h-6 px-2 rounded text-xs font-semibold uppercase ${getExecutionStatusBadgeClass(
                                execStatus.status,
                              )}`}
                            >
                              {getExecutionStatusLabel(execStatus.status)}
                            </Badge>
                          ) : execStatusIsError ? (
                            <span className="text-xs text-[#93000A]">
                              {execStatusError?.message ?? "Erro ao carregar status"}
                            </span>
                          ) : (
                            <span className="text-xs text-[#3B494A]">Carregando status…</span>
                          )}
                        </div>
                        {execStatus ? (
                          <>
                            {(() => {
                              const total = execStatus.total_steps ?? pipelineSteps.length;
                              const done = execStatus.steps_completed?.length ?? 0;
                              const pct = total > 0
                                ? Math.round((done / total) * 100)
                                : Math.round(Math.min(100, Math.max(0, execStatus.progress_percentage ?? 0)));
                              return (
                                <div>
                                  <div className="flex justify-between text-xs text-[#3B494A] mb-1">
                                    <span>Progresso</span>
                                    <span>{pct}%</span>
                                  </div>
                                  <div className="h-2 rounded-full bg-[#E8EFF0] overflow-hidden">
                                    <div
                                      className="h-full bg-[#00646B] transition-[width] duration-500 ease-out"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })()}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                              {execStatus.current_step ? (
                                <div>
                                  <div className="text-[#3B494A] text-xs">Etapa atual</div>
                                  <div className="font-medium text-[#151C1D] font-mono text-xs break-all">
                                    {execStatus.current_step}
                                  </div>
                                </div>
                              ) : null}
                              <div>
                                <div className="text-[#3B494A] text-xs">Etapas concluídas</div>
                                <div className="font-medium text-[#151C1D]">
                                  {execStatus.steps_completed?.length ?? 0} /{" "}
                                  {execStatus.total_steps ?? pipelineSteps.length}
                                </div>
                              </div>
                              <div>
                                <div className="text-[#3B494A] text-xs">Início</div>
                                <div className="font-medium text-[#151C1D]">
                                  {formatDateTime(execStatus.started_at)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[#3B494A] text-xs">Conclusão</div>
                                <div className="font-medium text-[#151C1D]">
                                  {formatDateTime(execStatus.completed_at)}
                                </div>
                              </div>
                              <div>
                                <div className="text-[#3B494A] text-xs">Duração total</div>
                                <div className="font-medium text-[#151C1D]">
                                  {formatDuration(execStatus.duration)}
                                </div>
                              </div>
                            </div>
                            {execStatus.simulation_name ? (
                              <div className="text-xs text-[#3B494A]">
                                Simulação:{" "}
                                <span className="font-medium text-[#151C1D]">
                                  {execStatus.simulation_name}
                                </span>
                              </div>
                            ) : null}
                            {execStatus.error_message ? (
                              <div className="rounded-xl border border-[#F5C2C7] bg-[#FFF5F5] px-3 py-2 text-xs text-[#93000A]">
                                {execStatus.error_message}
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-sm text-[#3B494A]">
                        Inicie a pipeline para acompanhar o progresso das etapas
                        configuradas.
                      </p>
                    )}

                    {pipelineSteps.length === 0 ? (
                      <div className="text-sm text-[#3B494A]">Nenhuma etapa configurada.</div>
                    ) : (
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-[#3B494A] mb-3">
                          Etapas ({pipelineConfigLabel(pipelineSteps.length)})
                        </div>
                        <div className="flex flex-col gap-3 max-w-3xl">
                          {pipelineSteps.map((step, index) => {
                            const stepState = getPipelineStepState(step, execStatus);
                            const rowStyle = pipelineStepRowClass(stepState);
                            const detail = execStatus?.step_details?.[step] as StepDetail | undefined;
                            return (
                              <div key={`${step}-${index}`} className="flex flex-col">
                                <div
                                  className={`min-h-14 px-4 rounded-2xl border bg-linear-to-r shadow-[0_2px_8px_rgba(0,0,0,0.06)] flex items-center gap-3 ${rowStyle}`}
                                >
                                  <PipelineStepIcon state={stepState} index={index} />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-semibold text-[#1D2627] tracking-[0.01em] break-all">
                                      {step}
                                    </span>
                                    {detail && (
                                      <div className="flex gap-3 mt-0.5 text-[11px] text-[#6B797A]">
                                        {detail.executed_at && (
                                          <span>{formatDateTime(detail.executed_at)}</span>
                                        )}
                                        {detail.duration != null && (
                                          <span>{formatDuration(detail.duration)}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <Badge
                                    variant="outline"
                                    className={`ml-auto shrink-0 text-[10px] font-semibold uppercase ${pipelineStepBadgeClass(
                                      stepState,
                                    )}`}
                                  >
                                    {pipelineStepLabel(stepState)}
                                  </Badge>
                                </div>
                                {index < pipelineSteps.length - 1 ? (
                                  <div className="pl-7 py-1 text-[#6B797A] text-xs">↓</div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

